import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { useRootSelector } from '../../../../store/hooks';
import { selectPageConfig } from '../../../../store/features/pageConfig';
import {
  concatPath,
  isAbsoluteUrl,
  isFileUrl,
  isMailToUrl,
  isTelephoneUrl,
} from '../../../../helpers';
import { localizePath } from '../../../../helpers/locale';
import { selectOptions } from '../../../../store/features/options';
import addPreviewPrefixToUrl from '../../../../helpers/preview/addPreviewPrefixToUrl';

export interface UseCustomizedSiteURLProps {
  url?: string;
  pathPrefix?: string | string[];
  query?: Record<string, any>;
  disableSitePath?: boolean;
}

export function useCustomizedSiteURL(props: UseCustomizedSiteURLProps) {
  const { i18n } = useTranslation();
  const { url, pathPrefix, query, disableSitePath } = props;
  const { sitePath, location } = useRootSelector(selectPageConfig);
  const options = useRootSelector(selectOptions);
  const primaryLocale = options?.locales?.primary;

  const isExternalURL = useMemo(
    () =>
      isAbsoluteUrl(url) ||
      isFileUrl(url) ||
      isMailToUrl(url) ||
      isTelephoneUrl(url),
    [url],
  );
  const queryString = useMemo(
    () => qs.stringify(query, { addQueryPrefix: true }),
    [query],
  );

  if (!url) return;

  if (isExternalURL) return `${url}${queryString}`;

  const pathPrefixArray = Array.isArray(pathPrefix) ? pathPrefix : [pathPrefix];
  const sitePathPrefix = disableSitePath ? '' : sitePath;
  const customizedUrl = concatPath(
    [sitePathPrefix, ...pathPrefixArray].concat([url]),
  );
  const finalUrl = `${customizedUrl}${queryString}`;
  const localizedUrl =
    disableSitePath || !primaryLocale
      ? finalUrl
      : localizePath(finalUrl, i18n.language, primaryLocale);

  return addPreviewPrefixToUrl({
    url: localizedUrl,
    pathname: location?.pathname,
    hostname: location?.hostname,
  });
}
